import type { CSSProperties } from "react"
import { cn } from "@/lib/cn"

/*
 * MARK V — loader "Traço" (Claude Design, bundle "Mark V Loader", variante 1A).
 * O monograma V se desenha, preenche com o gradiente da marca e sai.
 *
 * Este é o ÚNICO indicador de atividade indeterminada do app. Não confundir com
 * Skeleton: aquele é placeholder de conteúdo com layout conhecido (resolve CLS);
 * este é "algo está acontecendo, duração desconhecida".
 *
 * Server Component de propósito: sem hook e sem diretiva de cliente. Duas
 * decisões sustentam isso, e as duas divergem do arquivo do design:
 *
 *   1. O <clipPath> do desenho era redundante (recortava o contorno externo
 *      usando o próprio contorno externo). Removido.
 *   2. O id do gradiente é constante POR TINTA, não sorteado por instância.
 *      Sem isso precisaríamos de useId(), que é hook, que empurraria o
 *      primitivo para o cliente e mandaria JS para toda rota só para desenhar
 *      um SVG animado por CSS. Ver o bloco dos ids abaixo: são dois, e o
 *      motivo de não ser um só custou um bug de tinta invisível.
 *
 * A terceira divergência é o entalhe. O design pintava o miolo do V com a cor
 * do palco e exigia override manual por contexto (o verde do botão primário,
 * escrito à mão). Isso erra em hover, sobre o scrim do overlay e sobre o hero
 * em gradiente. Aqui o miolo é recorte de verdade, via fill-rule="evenodd":
 * furo transparente que funciona sobre qualquer fundo, em qualquer tema.
 *
 * Cor mora em --markv-loader-* (tokens.css): verde no light, branco no dark.
 * Motion mora em src/styles/loader.css.
 */

/** Contorno externo do monograma. */
const PATH_EXTERNO =
  "M69.6 133.7 Q20 30 128.4 68.3 L363.3 151 Q434 176 504.7 151 L739.6 68.3 Q848 30 798.4 133.7 L475 810.3 Q434 896 393 810.3 Z"

/** Entalhe interno (o "vazado" do V). */
const PATH_INTERNO =
  "M331 399.5 Q315 365 349.4 381.2 L406.8 408.2 Q434 421 461.1 408 L516.7 381.4 Q551 365 535.2 399.5 L447.3 591.9 Q434 621 420.5 592 Z"

const VIEWBOX_LARGURA = 868
const VIEWBOX_ALTURA = 910

const TAMANHO_PADRAO = 32
const TAMANHO_MINIMO = 12
const DURACAO_PADRAO_MS = 1800

const STROKE_EXTERNO = 26
const STROKE_INTERNO = 20

/*
 * Um id por TINTA, não um id só.
 *
 * `url(#id)` resolve sempre para a primeira definição do documento, e as stops
 * usam var(--markv-loader-*), que resolvem no elemento onde o gradiente foi
 * DECLARADO. Com um id único para todo mundo, a tinta do primeiro loader da
 * página vazava para os demais: um Button primary (inverse, branco) no topo
 * deixava todo o resto branco no branco, invisível no tema claro.
 *
 * Duas tintas, dois ids. O tema não entra na conta porque é global: todas as
 * instâncias de uma página estão no mesmo data-theme.
 */
const GRADIENTE_ID_PADRAO = "markv-trace-grad"
const GRADIENTE_ID_INVERSO = "markv-trace-grad-inverse"

/** Escala de uso sancionada pelo design. */
export const MARKV_TRACE_SIZE = {
  button: 20,
  inline: 32,
  card: 48,
  overlay: 72,
  splash: 96,
} as const

/**
 * Fator de espessura do traço por tamanho. Abaixo de 64px o contorno precisa
 * engrossar para continuar legível; abaixo de 32px, mais ainda.
 */
export function traceStrokeScale(size: number): number {
  if (size < 32) return 1.9
  if (size < 64) return 1.35
  return 1
}

function normalizaTamanho(size: number | undefined): number {
  if (size === undefined) return TAMANHO_PADRAO
  if (!Number.isFinite(size) || size <= 0) return TAMANHO_PADRAO
  return Math.max(TAMANHO_MINIMO, Math.round(size))
}

function normalizaDuracao(ms: number | undefined): number {
  if (ms === undefined) return DURACAO_PADRAO_MS
  if (!Number.isFinite(ms) || ms <= 0) return DURACAO_PADRAO_MS
  return Math.round(ms)
}

export type MarkVTraceLoaderProps = {
  /** Lado horizontal em px. Escala: 20 botão, 32 linha, 48 card, 72 overlay, 96 splash. */
  size?: number
  /** Nome acessível. String vazia esconde do leitor de tela (use quando o texto ao lado já anuncia). */
  label?: string
  /** Força a tinta branca nos dois temas. Para uso sobre superfície de marca. */
  inverse?: boolean
  /** Duração do ciclo em ms. */
  durationMs?: number
  /**
   * Escape hatch para o id do gradiente. Só necessário se você sobrescrever
   * --markv-loader-* por fora: aí esta instância precisa do próprio id, senão
   * divide a definição com as outras e a tinta vaza.
   */
  gradientId?: string
  className?: string
}

export function MarkVTraceLoader({
  size,
  label = "Carregando",
  inverse = false,
  durationMs,
  gradientId,
  className,
}: MarkVTraceLoaderProps) {
  const idGradiente =
    gradientId ?? (inverse ? GRADIENTE_ID_INVERSO : GRADIENTE_ID_PADRAO)
  const largura = normalizaTamanho(size)
  const altura = Math.round((largura * VIEWBOX_ALTURA) / VIEWBOX_LARGURA)
  const escala = traceStrokeScale(largura)
  const duracao = normalizaDuracao(durationMs)

  // Vazio significa decorativo: quem anuncia é o texto ao lado (botão, linha).
  const decorativo = label === ""

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_LARGURA} ${VIEWBOX_ALTURA}`}
      width={largura}
      height={altura}
      role={decorativo ? undefined : "img"}
      aria-label={decorativo ? undefined : label}
      aria-hidden={decorativo || undefined}
      focusable="false"
      className={cn("mv-trace", inverse && "mv-trace--inverse", className)}
      style={{ "--mv-trace-dur": `${duracao}ms` } as CSSProperties}
    >
      <defs>
        <linearGradient id={idGradiente} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--markv-loader-1)" />
          <stop
            offset="0.52"
            stopColor="var(--markv-loader-2)"
            stopOpacity="var(--markv-loader-o2)"
          />
          <stop
            offset="1"
            stopColor="var(--markv-loader-3)"
            stopOpacity="var(--markv-loader-o3)"
          />
        </linearGradient>
      </defs>

      {/* Preenchimento: um path só, com os dois contornos. O evenodd abre o
          entalhe como furo real, então o miolo mostra o fundo de verdade. */}
      <path
        className="mv-trace__fill"
        d={`${PATH_EXTERNO} ${PATH_INTERNO}`}
        fillRule="evenodd"
        fill={`url(#${idGradiente})`}
      />

      <path
        className="mv-trace__draw"
        d={PATH_EXTERNO}
        fill="none"
        stroke="var(--markv-loader-2)"
        strokeWidth={Math.round(STROKE_EXTERNO * escala)}
        strokeLinejoin="round"
        strokeDasharray="2800"
      />

      <path
        className="mv-trace__draw-in"
        d={PATH_INTERNO}
        fill="none"
        stroke="var(--markv-loader-1)"
        strokeWidth={Math.round(STROKE_INTERNO * escala)}
        strokeLinejoin="round"
        strokeDasharray="760"
      />
    </svg>
  )
}
